export type DualPaneNavNode = {
	label: string;
	to: string;
	subcategories?: DualPaneNavNode[];
};

/** True when this node’s own target matches the active route. */
export function isNavTargetActive(
	activeRoute: string | undefined,
	to: string,
): boolean {
	return Boolean(activeRoute) && activeRoute === to;
}

/** True when this node or any descendant matches the active route. */
export function navItemContainsActiveRoute(
	node: DualPaneNavNode,
	activeRoute: string | undefined,
): boolean {
	if (!activeRoute) {
		return false;
	}

	if (node.to === activeRoute) {
		return true;
	}

	return (node.subcategories ?? []).some((child) =>
		navItemContainsActiveRoute(child, activeRoute),
	);
}

/**
 * Whether a sidebar row should show the active style.
 * - Leaf rows: match their own `to`.
 * - Parent rows (with children): match only when a **descendant** is active.
 *   Ignore the parent's own `to` — group placeholders often reuse a sibling
 *   target (e.g. `dashboard`), which must not light up sibling leaves together.
 */
export function isNavNodeActive(
	node: DualPaneNavNode,
	activeRoute: string | undefined,
): boolean {
	if (!activeRoute) {
		return false;
	}

	const children = node.subcategories ?? [];
	if (children.length > 0) {
		return children.some((child) => navItemContainsActiveRoute(child, activeRoute));
	}

	return node.to === activeRoute;
}

/**
 * Parent labels that should be expanded so the active child row is visible.
 * Does not include the active leaf itself.
 */
export function collectExpandedLabelsForActiveRoute(
	items: DualPaneNavNode[],
	activeRoute: string | undefined,
): string[] {
	if (!activeRoute) {
		return [];
	}

	const labels: string[] = [];

	function walk(nodes: DualPaneNavNode[], ancestors: string[]): boolean {
		for (const node of nodes) {
			const path = [...ancestors, node.label];
			const children = node.subcategories ?? [];

			if (children.length > 0 && walk(children, path)) {
				labels.push(node.label);
				return true;
			}

			if (node.to === activeRoute && ancestors.length > 0) {
				return true;
			}
		}

		return false;
	}

	walk(items, []);
	return labels;
}
